import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { generatePDFDocumentFromRef } from "../../../util/generatePDF.tsx";
import Spinner from "../../../components/Spinner/Spinner.tsx";
import { submitContact } from "../../../api/contact";

interface TaxDataState {
  status: "idle" | "success";
  data: { tax: number; rate: number };
}

interface TaxTableData {
  taxBand: string;
  rate: string;
  taxableSum: string;
  tax: string;
}

interface TaxTableState {
  status: "idle" | "success";
  data: TaxTableData[];
}

interface StampDutyPage2Props {}

export default function StampDutyPage2(
  props: StampDutyPage2Props
): JSX.Element {
  const location = useLocation();
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);
  const [contactSuccess, setContactSuccess] = useState(false);

  const [isFirstHome, setIsFirstHome] = useState<boolean>(true);
  const [propertyPrice, setPropertyPrice] = useState<number>(0);
  const [propertyPriceDisplay, setPropertyPriceDisplay] = useState<string>("0");
  const [isNonUKResident, setIsNonUKResident] = useState<boolean>(false);
  const [taxTable, setTaxTable] = useState<TaxTableState>({
    status: "idle",
    data: [],
  });
  const [taxData, setTaxData] = useState<TaxDataState>({
    status: "idle",
    data: { tax: 0, rate: 0 },
  });
  const [isGeneratingPDF, setIsGeneratingPDF] = useState<boolean>(false);
  const tableRef = useRef<HTMLDivElement>(null);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setContactError(null);
    setContactSuccess(false);
    setContactSubmitting(true);

    try {
      await submitContact({
        name: contactName.trim(),
        email: contactEmail.trim(),
        phone: contactPhone.trim() || undefined,
        subject: "Stamp Duty Tax Advisory",
        message: contactMessage.trim(),
        sourcePage: location.pathname,
        enquiryType: "general",
      });

      setContactSuccess(true);
      setContactName("");
      setContactEmail("");
      setContactPhone("");
      setContactMessage("");
    } catch (err) {
      setContactError(
        err instanceof Error ? err.message : "Failed to send your enquiry"
      );
    } finally {
      setContactSubmitting(false);
    }
  };

  function handleCalculate(e: React.FormEvent) {
    e.preventDefault();
    let bands = [
      { lower: 0, upper: 125000, rate: 0.0 },
      { lower: 125000, upper: 250000, rate: 0.02 },
      { lower: 250000, upper: 925000, rate: 0.05 },
      { lower: 925000, upper: 1500000, rate: 0.1 },
      { lower: 1500000, upper: Infinity, rate: 0.12 },
    ];

    const nonUKResidentSurcharge =
      isNonUKResident &&
      (isFirstHome || (!isFirstHome && +propertyPrice >= 40000))
        ? 0.02
        : 0;
    const additionalPropertySurcharge =
      !isFirstHome && +propertyPrice >= 40000 ? 0.05 : 0;

    if (isFirstHome && +propertyPrice <= 500000) {
      bands[0].upper = 300000;
      bands[1].lower = 300000;
      bands[2].lower = 300000;
      bands.splice(1, 1);
    }

    if (!isFirstHome && +propertyPrice < 40000) {
      bands = [{ lower: 0, upper: 39999, rate: 0.0 }];
    }

    let tax = 0;
    let tableData: TaxTableData[] = [];
    let price = +propertyPrice;

    bands.forEach((band) => {
      if (price > band.lower) {
        let effectiveRate =
          band.rate + nonUKResidentSurcharge + additionalPropertySurcharge;
        const taxableSum = Math.min(price, band.upper) - band.lower;
        const taxForBand = taxableSum * effectiveRate;
        tax += taxForBand;

        let taxBandText =
          band.upper === Infinity
            ? "£1,500,000+"
            : `£${band.lower.toLocaleString()} - £${band.upper.toLocaleString()}`;

        tableData.push({
          taxBand: taxBandText,
          rate: `${(effectiveRate * 100).toFixed(0)}%`,
          taxableSum: `£${taxableSum.toLocaleString()}`,
          tax: `£${taxForBand.toFixed(2)}`,
        });
      }
    });

    setTaxTable({ status: "success", data: tableData });
    setTaxData({
      status: "success",
      data: {
        rate: tax ? parseFloat(((tax / price) * 100).toFixed(1)) : 0,
        tax: +tax.toFixed(2),
      },
    });
  }

  const handleBuyerPositionChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    if (value === "main") {
      setIsFirstHome(true);
    } else if (value === "investor") {
      setIsFirstHome(false);
    }
  };

  // NEW: Make 'cards' clickable too (for accessibility/ui)
  const handleBuyerCardClick = (choice: "main" | "investor") => {
    setIsFirstHome(choice === "main");
  };

  function handlePropertyPriceInput(e: React.ChangeEvent<HTMLInputElement>) {
    let inputValue = e.target.value;
    inputValue = inputValue.replace(/[^0-9]/g, "");
    const numericValue = parseInt(inputValue, 10);

    if (!isNaN(numericValue)) {
      setPropertyPrice(numericValue);
      setPropertyPriceDisplay(numericValue.toLocaleString());
    } else {
      setPropertyPrice(0);
      setPropertyPriceDisplay("0");
    }
  }

  function handleDownloadPDF() {
    setIsGeneratingPDF(true);
  }

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("language", "english"); // Assuming English is the default language
    window.history.pushState({}, "", url);
  }, []);

  useEffect(() => {
    (async () => {
      if (isGeneratingPDF && tableRef.current) {
        try {
          const doc = await generatePDFDocumentFromRef(tableRef);
          if (doc) {
            doc.save(`Stamp Duty Calculation.pdf`);
            setIsGeneratingPDF(false);
          }
        } catch (error: any) {
          console.error(error);
        } finally {
          setIsGeneratingPDF(false);
        }
      }
    })();
  }, [isGeneratingPDF, tableRef]);

  const showResult =
    taxData.status === "success" && taxTable.status === "success";

  return (
    <>
      <Helmet>
        <title>Stamp Duty Calculator | Letting Engine</title>
        <meta
          name="description"
          content="Use our stamp duty calculator, Letting Engine is a premier residential property letting agency based in London, specializing in luxury rentals."
        />
        <meta
          name="keywords"
          content="residential property letting agency, real estate, london, stamp duty calculator, stamp duty"
        />
      </Helmet>

      {/* HEADER */}
      <section className="page-header py-32 pb-16 max-w-[1600px] mx-auto px-[5%]">
        <h1 className="font-teko text-5xl tracking-[-1px] mb-4">
          Stamp Duty Land Tax (SDLT)
          <br />
          Information and Calculator 2025/2026
        </h1>
        <p className="font-manrope text-lg text-text-slate max-w-[700px] leading-relaxed">
          Stamp Duty Land Tax (SDLT) is a tax paid on property or land purchases
          in England and Northern Ireland. The tax varies if the property is in
          Scotland (Land and Buildings Transaction Tax) or Wales (Land
          Transaction Tax). SDLT applies when you buy a freehold property, a new
          or existing leasehold, a property through a shared ownership scheme,
          or when land or property is transferred in exchange for payment.
        </p>
      </section>

      {/* MAIN LAYOUT */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] lg:gap-10 max-w-[1600px] mx-auto px-[5%] pb-32 ">
        {/* <div className="grid grid-cols-1 sm:grid-cols-layout-grid gap-6 md:p-16 max-w-[1600px] mx-auto px-[5%] pb-32 "> */}
        {/* LEFT: MAIN CONTENT */}
        <div className="main-content flex flex-col gap-16">
          {/* CALCULATOR */}
          <div className="bg-bg-white border border-solid border-[rgba(0,0,0,0.05)] p-6 md:p-16 relative overflow-hidden clip-path-[polygon(0_0,100%_0,100%_98%,97%_100%,0_100%)] shadow-soft">
            <h3 className="font-teko text-3xl mb-12 text-text-obsidian border-b border-b-solid border-b-[rgba(0,0,0,0.05)] pb-4">
              Calculation
            </h3>

            <form
              onSubmit={handleCalculate}
              className="grid gap-10"
              onChange={() => {
                setTaxData({ status: "idle", data: { tax: 0, rate: 0 } });
                setTaxTable({ status: "idle", data: [] });
              }}
            >
              <div className="mb-10">
                <label className="block font-teko text-lg mb-3 uppercase text-text-obsidian tracking-wide">
                  Purchase Price
                </label>
                <div className="flex items-center border-b-2 border-b-solid border-b-[#ddd] focus-within:border-brand-yellow transition-all duration-300">
                  <span className="font-manrope text-2xl text-text-slate pl-2">
                    £
                  </span>
                  <input
                    type="text"
                    className="w-full border-none p-4 text-xl font-manrope text-text-obsidian outline-none bg-transparent hideSpinner"
                    placeholder="0"
                    value={propertyPriceDisplay}
                    onChange={handlePropertyPriceInput}
                    id="purchasePriceInput"
                  />
                </div>
              </div>
              <div className="mb-10">
                <label className="block font-teko text-lg mb-3 uppercase text-text-obsidian tracking-wide">
                  Buyer Position
                </label>
                <div className="flex gap-4 flex-wrap flex-col xl:flex-row">
                  {/* "First home" option */}
                  <label
                    className={[
                      "flex-1 cursor-pointer relative min-w-[150px] transition-colors duration-200",
                      isFirstHome
                        ? "border-2 border-brand-yellow !border-solid rounded"
                        : "border-2 border-transparent rounded",
                    ].join(" ")}
                    htmlFor="buyer-main"
                    style={{}}
                    onClick={() => handleBuyerCardClick("main")}
                  >
                    <input
                      type="radio"
                      id="buyer-main"
                      name="buyer"
                      value="main"
                      className="sr-only"
                      onChange={handleBuyerPositionChange}
                      checked={isFirstHome}
                      tabIndex={-1}
                    />
                    <div
                      className={[
                        "flex flex-col items-center justify-center h-20 border border-solid bg-bg-porcelain transition-all duration-300 transform",
                        isFirstHome
                          ? "bg-text-obsidian text-white border-brand-yellow shadow-[0_10px_20px_rgba(0,0,0,0.1)]"
                          : "border-[#ddd]",
                      ].join(" ")}
                    >
                      <div className="transform text-center">
                        <span className="font-teko text-lg leading-none uppercase">
                          Property is a first home
                        </span>
                        <span
                          className={[
                            "text-xs text-text-slate block mt-1",
                            isFirstHome ? "text-[#aaa]" : "",
                          ].join(" ")}
                        >
                          Standard or Relief Rate
                        </span>
                      </div>
                    </div>
                  </label>
                  {/* "Buy to let or second home" option */}
                  <label
                    className={[
                      "flex-1 cursor-pointer relative min-w-[150px] transition-colors duration-200",
                      !isFirstHome
                        ? "border-2 border-brand-yellow !border-solid rounded"
                        : "border-2 border-transparent rounded",
                    ].join(" ")}
                    htmlFor="buyer-investor"
                    onClick={() => handleBuyerCardClick("investor")}
                  >
                    <input
                      type="radio"
                      id="buyer-investor"
                      name="buyer"
                      value="investor"
                      className="sr-only"
                      onChange={handleBuyerPositionChange}
                      checked={!isFirstHome}
                      tabIndex={-1}
                    />
                    <div
                      className={[
                        "flex flex-col items-center justify-center h-20 border border-solid bg-bg-porcelain transition-all duration-300 transform ",
                        !isFirstHome
                          ? "bg-text-obsidian text-white border-brand-yellow shadow-[0_10px_20px_rgba(0,0,0,0.1)]"
                          : "border-[#ddd]",
                      ].join(" ")}
                    >
                      <div className="transform text-center">
                        <span className="font-teko text-lg leading-none uppercase">
                          Property is a buy to let or second home
                        </span>
                        <span
                          className={[
                            "text-xs text-text-slate block mt-1",
                            !isFirstHome ? "text-[#aaa]" : "",
                          ].join(" ")}
                        >
                          Additional Rate
                        </span>
                      </div>
                    </div>
                  </label>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <input
                  type="checkbox"
                  id="nonUkResident"
                  className="checkbox checkbox-warning"
                  checked={isNonUKResident}
                  onChange={(e) => setIsNonUKResident(e.target.checked)}
                />
                <label
                  htmlFor="nonUkResident"
                  className="font-manrope text-base text-text-obsidian uppercase tracking-wider"
                >
                  Buyer is a non-UK resident
                </label>
              </div>

              <button
                type="submit"
                className="w-full p-6 bg-brand-yellow mt-8 border-none text-text-obsidian font-teko text-2xl uppercase tracking-wide cursor-pointer transition-all duration-300 clip-path-[polygon(0_0,100%_0,100%_85%,97%_100%,0_100%)] hover:translate-y-[-3px] hover:shadow-[0_15px_30px_rgba(255,196,0,0.2)]"
              >
                Calculate Liability
              </button>
            </form>

            {showResult && (
              <div className="bg-bg-porcelain text-text-obsidian p-12 mt-16 relative max-w-full min-w-0 overflow-hidden">
                <div className="flex flex-col sm:flex-row justify-between items-baseline mb-4">
                  <span className="font-manrope uppercase tracking-wider text-sm opacity-70">
                    Total SDLT Due
                  </span>
                  <span className="font-manrope uppercase tracking-wider text-sm bg-[#23272a] text-brand-yellow px-3 py-1 rounded">
                    Effective Rate: {taxData.data.rate}%
                  </span>
                </div>
                <div className="font-teko text-6xl text-brand-yellow">
                  £{taxData.data.tax.toLocaleString()}
                </div>

                <h4 className="font-teko text-3xl mt-12 text-text-obsidian border-b border-b-solid border-b-[rgba(0,0,0,0.08)] pb-4">
                  Stamp Duty Calculation Breakdown
                </h4>
                <div className="w-full max-w-full min-w-0 overflow-x-auto">
                  <table className="min-w-[680px] w-full border-collapse mt-8">
                    <thead>
                      <tr>
                        <th className="text-left p-4 pr-6 font-teko text-lg uppercase bg-[#f3f3f3] text-text-obsidian tracking-wider">
                          Tax Band
                        </th>
                        <th className="text-left p-4 pr-6 font-teko text-lg uppercase bg-[#f3f3f3] text-text-obsidian tracking-wider">
                          %
                        </th>
                        <th className="text-left p-4 pr-6 font-teko text-lg uppercase bg-[#f3f3f3] text-text-obsidian tracking-wider">
                          Taxable Sum
                        </th>
                        <th className="text-right p-4 pr-6 font-teko text-lg uppercase bg-[#f3f3f3] text-text-obsidian tracking-wider">
                          Tax
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {taxTable.data.map((row, index) => (
                        <tr
                          key={index}
                          className="border-b border-b-solid border-b-[rgba(0,0,0,0.07)] last:border-b-0"
                        >
                          <td className="p-4 pr-6 font-manrope text-base text-text-obsidian opacity-90">
                            {row.taxBand}
                          </td>
                          <td className="p-4 pr-6 font-manrope text-base text-text-obsidian opacity-90">
                            {row.rate}
                          </td>
                          <td className="p-4 pr-6 font-manrope text-base text-text-obsidian opacity-90">
                            {row.taxableSum}
                          </td>
                          <td className="p-4 pr-6 font-manrope text-base text-text-obsidian opacity-90 text-right">
                            {row.tax}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="font-manrope text-xs opacity-50 mt-8 leading-relaxed italic">
                  The amounts in the table are for reference only and are
                  subject to actual occurrence; If you reside continuously for
                  183 days after coming to the UK, you may be eligible for a 2%
                  tax refund for overseas buyers with a visa that allows
                  continuous residence.
                </p>
                <button
                  className="btn btn-warning btn-sm m-0 mt-12 block mx-auto"
                  onClick={handleDownloadPDF}
                >
                  {isGeneratingPDF ? (
                    <>
                      <Spinner /> Downloading PDF...
                    </>
                  ) : (
                    <>Download PDF</>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* TEXT CONTENT */}
          <div className="bg-bg-white border border-solid border-[rgba(0,0,0,0.05)] p-6 md:p-16 relative overflow-hidden clip-path-[polygon(0_0,100%_0,100%_98%,97%_100%,0_100%)] shadow-soft">
            <h3 className="font-teko text-3xl mb-12 text-text-obsidian border-b border-b-solid border-b-[rgba(0,0,0,0.05)] pb-4">
              Stamp Duty Rates
            </h3>

            <h4 className="font-teko text-2xl mt-12 mb-6 text-text-obsidian">
              Standard Residential Properties
            </h4>
            <table className="w-full border-collapse mb-8 bg-bg-porcelain">
              <thead>
                <tr>
                  <th className="text-left p-4 pr-6 font-teko text-lg uppercase bg-[#eee] text-text-obsidian tracking-wide">
                    Property Value
                  </th>
                  <th className="text-left p-4 pr-6 font-teko text-lg uppercase bg-[#eee] text-text-obsidian tracking-wide">
                    SDLT Rate
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-4 pr-6 border-b border-b-solid border-b-[#ddd] font-manrope text-base text-text-slate">
                    Up to £125,000
                  </td>
                  <td className="p-4 pr-6 border-b border-b-solid border-b-[#ddd] font-manrope text-base text-text-obsidian font-semibold text-right">
                    0%
                  </td>
                </tr>
                <tr>
                  <td className="p-4 pr-6 border-b border-b-solid border-b-[#ddd] font-manrope text-base text-text-slate">
                    £125,001 to £250,000
                  </td>
                  <td className="p-4 pr-6 border-b border-b-solid border-b-[#ddd] font-manrope text-base text-text-obsidian font-semibold text-right">
                    2%
                  </td>
                </tr>
                <tr>
                  <td className="p-4 pr-6 border-b border-b-solid border-b-[#ddd] font-manrope text-base text-text-slate">
                    £250,001 to £925,000
                  </td>
                  <td className="p-4 pr-6 border-b border-b-solid border-b-[#ddd] font-manrope text-base text-text-obsidian font-semibold text-right">
                    5%
                  </td>
                </tr>
                <tr>
                  <td className="p-4 pr-6 border-b border-b-solid border-b-[#ddd] font-manrope text-base text-text-slate">
                    £925,001 to £1.5 million
                  </td>
                  <td className="p-4 pr-6 border-b border-b-solid border-b-[#ddd] font-manrope text-base text-text-obsidian font-semibold text-right">
                    10%
                  </td>
                </tr>
                <tr>
                  <td className="p-4 pr-6 font-manrope text-base text-text-slate">
                    Above £1.5 million
                  </td>
                  <td className="p-4 pr-6 font-manrope text-base text-text-obsidian font-semibold text-right">
                    12%
                  </td>
                </tr>
              </tbody>
            </table>

            <h4 className="font-teko text-2xl mt-12 mb-6 text-text-obsidian">
              First-Time Buyers
            </h4>
            <p className="font-manrope text-base leading-relaxed text-text-slate mb-4">
              Relief is available for first-time buyers purchasing properties
              worth £500,000 or less.
            </p>
            <table className="w-full border-collapse mb-8 bg-bg-porcelain">
              <thead>
                <tr>
                  <th className="text-left p-4 pr-6 font-teko text-lg uppercase bg-[#eee] text-text-obsidian tracking-wide">
                    Property Value
                  </th>
                  <th className="text-left p-4 pr-6 font-teko text-lg uppercase bg-[#eee] text-text-obsidian tracking-wide">
                    SDLT Rate
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-4 pr-6 border-b border-b-solid border-b-[#ddd] font-manrope text-base text-text-slate">
                    Up to £300,000
                  </td>
                  <td className="p-4 pr-6 border-b border-b-solid border-b-[#ddd] font-manrope text-base text-text-obsidian font-semibold text-right">
                    0%
                  </td>
                </tr>
                <tr>
                  <td className="p-4 pr-6 border-b border-b-solid border-b-[#ddd] font-manrope text-base text-text-slate">
                    £300,001 to £500,000
                  </td>
                  <td className="p-4 pr-6 border-b border-b-solid border-b-[#ddd] font-manrope text-base text-text-obsidian font-semibold text-right">
                    5%
                  </td>
                </tr>
                <tr>
                  <td className="p-4 pr-6 font-manrope text-base text-text-slate">
                    Properties over £500,000
                  </td>
                  <td className="p-4 pr-6 font-manrope text-base text-text-obsidian font-semibold text-right">
                    Standard Rates Apply
                  </td>
                </tr>
              </tbody>
            </table>

            <h4 className="font-teko text-2xl mt-12 mb-6 text-text-obsidian">
              Surcharges & Thresholds
            </h4>
            <div className="bg-[#FFF8E1] border-l-4 border-l-solid border-l-brand-yellow p-6 mb-6">
              <h5 className="font-teko text-xl mb-2 text-text-obsidian">
                Additional Properties
              </h5>
              <p className="font-manrope text-base text-[#554400] leading-relaxed">
                An additional 5% on top of standard SDLT rates applies for
                buying new residential properties if you already own another
                property. Properties under £40,000 are exempt from this
                surcharge.
              </p>
            </div>
            <div className="bg-[#FFF8E1] border-l-4 border-l-solid border-l-brand-yellow p-6 mb-6">
              <h5 className="font-teko text-xl mb-2 text-text-obsidian">
                Non-UK Residents
              </h5>
              <p className="font-manrope text-base text-[#554400] leading-relaxed">
                A 2% surcharge applies for non-UK residents buying residential
                property in England and Northern Ireland.
              </p>
            </div>

            <h4 className="font-teko text-2xl mt-12 mb-6 text-text-obsidian">
              Eligibility & Reliefs
            </h4>
            <p className="font-manrope text-base leading-relaxed text-text-slate mb-4">
              Various SDLT reliefs are available in specific situations:
            </p>
            <ul className="list-none p-0 mb-8">
              <li className="relative pl-5 mb-3 font-manrope text-base text-text-slate leading-relaxed before:content-[''] before:absolute before:left-0 before:top-2.5 before:w-1.5 before:h-1.5 before:bg-brand-yellow before:transform before:skew-x-[var(--skew)]">
                First-time buyers (as detailed above).
              </li>
              <li className="relative pl-5 mb-3 font-manrope text-base text-text-slate leading-relaxed before:content-[''] before:absolute before:left-0 before:top-2.5 before:w-1.5 before:h-1.5 before:bg-brand-yellow before:transform before:skew-x-[var(--skew)]">
                Multiple Dwellings Relief (MDR) for bulk purchases.
              </li>
              <li className="relative pl-5 mb-3 font-manrope text-base text-text-slate leading-relaxed before:content-[''] before:absolute before:left-0 before:top-2.5 before:w-1.5 before:h-1.5 before:bg-brand-yellow before:transform before:skew-x-[var(--skew)]">
                Property transfers resulting from divorce or separation.
              </li>
              <li className="relative pl-5 mb-3 font-manrope text-base text-text-slate leading-relaxed before:content-[''] before:absolute before:left-0 before:top-2.5 before:w-1.5 before:h-1.5 before:bg-brand-yellow before:transform before:skew-x-[var(--skew)]">
                Purchases by charities or for charitable purposes.
              </li>
            </ul>

            <h4 className="font-teko text-2xl mt-12 mb-6 text-text-obsidian">
              Payment and Deadlines
            </h4>
            <p className="font-manrope text-base leading-relaxed text-text-slate mb-4">
              Stamp Duty Land Tax must be filed and paid within{" "}
              <strong className="font-semibold">14 days</strong> of the
              effective transaction date (usually the completion date). Failure
              to file on time can result in penalties and interest.
            </p>

            <h4 className="font-teko text-2xl mt-12 mb-6 text-text-obsidian">
              Exemptions
            </h4>
            <p className="font-manrope text-base leading-relaxed text-text-slate mb-4">
              You may not need to pay SDLT or file a return if:
            </p>
            <ul className="list-none p-0 mb-8">
              <li className="relative pl-5 mb-3 font-manrope text-base text-text-slate leading-relaxed before:content-[''] before:absolute before:left-0 before:top-2.5 before:w-1.5 before:h-1.5 before:bg-brand-yellow before:transform before:skew-x-[var(--skew)]">
                No money or other payment changes hands for a property transfer.
              </li>
              <li className="relative pl-5 mb-3 font-manrope text-base text-text-slate leading-relaxed before:content-[''] before:absolute before:left-0 before:top-2.5 before:w-1.5 before:h-1.5 before:bg-brand-yellow before:transform before:skew-x-[var(--skew)]">
                Property is inherited under a will.
              </li>
              <li className="relative pl-5 mb-3 font-manrope text-base text-text-slate leading-relaxed before:content-[''] before:absolute before:left-0 before:top-2.5 before:w-1.5 before:h-1.5 before:bg-brand-yellow before:transform before:skew-x-[var(--skew)]">
                Property is transferred because of divorce or dissolution of a
                civil partnership.
              </li>
              <li className="relative pl-5 mb-3 font-manrope text-base text-text-slate leading-relaxed before:content-[''] before:absolute before:left-0 before:top-2.5 before:w-1.5 before:h-1.5 before:bg-brand-yellow before:transform before:skew-x-[var(--skew)]">
                Freehold property is purchased for less than £40,000.
              </li>
              <li className="relative pl-5 mb-3 font-manrope text-base text-text-slate leading-relaxed before:content-[''] before:absolute before:left-0 before:top-2.5 before:w-1.5 before:h-1.5 before:bg-brand-yellow before:transform before:skew-x-[var(--skew)]">
                Property is bought under alternative financial arrangements
                (e.g., Sharia law compliance).
              </li>
            </ul>
            <p className="font-manrope text-xs text-[#999] mt-12 pt-8 border-t border-t-solid border-t-[#eee] leading-relaxed italic">
              <strong className="font-semibold">Additional Information:</strong>{" "}
              This calculator is for general interest and should not replace
              professional financial advice. Note that Stamp Duty can often be
              added to mortgage loans, depending on your lender's specific
              terms.
            </p>
          </div>
        </div>

        {/* RIGHT: STICKY FORM */}
        <div className="relative w-full lg:w-[400px] mt-20 lg:mt-0">
          <div className="sticky top-32">
            <div className="bg-[var(--glass-surface)] backdrop-blur-[25px] border-[var(--glass-border)] p-6 md:p-12 shadow-[0_30px_60px_rgba(0,0,0,0.05)] clip-path-[polygon(10%_0,100%_0,100%_100%,0%_100%,0_5%)]">
              <h3 className="font-teko text-3xl text-text-obsidian mb-2">
                Tax Advisory
              </h3>
              <p className="font-manrope text-sm text-text-slate leading-relaxed mb-8">
                Complex portfolio? Speak to our asset management team for a
                tailored tax efficiency strategy.
              </p>
              <form className="grid gap-5" onSubmit={handleContactSubmit}>
                {contactSuccess && (
                  <p className="font-manrope text-sm text-green-700 bg-green-50 p-4 border border-solid border-green-200">
                    Thank you — your enquiry has been sent.
                  </p>
                )}
                {contactError && (
                  <p className="font-manrope text-sm text-red-700 bg-red-50 p-4 border border-solid border-red-200">
                    {contactError}
                  </p>
                )}
                <input
                  type="text"
                  className="w-full p-4 bg-bg-white border border-solid border-[rgba(0,0,0,0.1)] font-manrope transition-all duration-300 clip-path-[polygon(0_0,100%_0,100%_85%,98%_100%,0_100%)] focus:outline-none focus:border-brand-yellow"
                  placeholder="Full Name"
                  id="advisoryFullNameInput"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  required
                  disabled={contactSubmitting}
                />
                <input
                  type="email"
                  className="w-full p-4 bg-bg-white border border-solid border-[rgba(0,0,0,0.1)] font-manrope transition-all duration-300 clip-path-[polygon(0_0,100%_0,100%_85%,98%_100%,0_100%)] focus:outline-none focus:border-brand-yellow"
                  placeholder="Email Address"
                  id="advisoryEmailAddressInput"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  required
                  disabled={contactSubmitting}
                />
                <input
                  type="tel"
                  className="w-full p-4 bg-bg-white border border-solid border-[rgba(0,0,0,0.1)] font-manrope transition-all duration-300 clip-path-[polygon(0_0,100%_0,100%_85%,98%_100%,0_100%)] focus:outline-none focus:border-brand-yellow"
                  placeholder="Phone Number"
                  id="advisoryPhoneNumberInput"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  disabled={contactSubmitting}
                />
                <textarea
                  className="w-full p-4 bg-bg-white border border-solid border-[rgba(0,0,0,0.1)] font-manrope transition-all duration-300 clip-path-[polygon(0_0,100%_0,100%_85%,98%_100%,0_100%)] focus:outline-none focus:border-brand-yellow"
                  rows={3}
                  placeholder="I have a query about SDLT..."
                  id="advisoryMessageInput"
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  required
                  disabled={contactSubmitting}
                ></textarea>
                <button
                  type="submit"
                  className="w-full p-4 mt-4 bg-text-obsidian text-white border-none font-teko text-xl uppercase cursor-pointer transition-all duration-300 clip-path-[polygon(0_0,100%_0,100%_80%,95%_100%,0_100%)] hover:bg-brand-yellow hover:text-black disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={contactSubmitting}
                >
                  {contactSubmitting ? "Sending…" : "Request Call"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
